import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';

import { logger } from '../../datadog/logger';
import { isScreenSharingAtom, videoSDKAdapterAtom } from '../../video-sdk';
import {
  pictureInPictureParticipantIdAtom,
  remoteMediaStatusByParticipantIdAtom,
  shouldTriggerPictureInPictureAtom,
} from '../../video-sdk/twilio';

export function useTriggerPictureInPicture(remoteParticipantId: string, videoRef: React.RefObject<HTMLVideoElement>) {
  const [videoMetadataLoaded, setVideoMetadataLoaded] = useState(false);

  const adapter = useAtomValue(videoSDKAdapterAtom);
  const isLocalScreenShareMode = useAtomValue(isScreenSharingAtom);
  const remoteMediaStatusByParticipantId = useAtomValue(remoteMediaStatusByParticipantIdAtom);

  const [shouldTriggerPictureInPicture, setShouldTriggerPictureInPicture] = useAtom(shouldTriggerPictureInPictureAtom);
  const pictureInPictureParticipantId = useAtomValue(pictureInPictureParticipantIdAtom);

  const remoteParticipantIsPictureInPictureTarget = pictureInPictureParticipantId === remoteParticipantId;
  const isRemoteVideoOff = remoteMediaStatusByParticipantId[remoteParticipantId]?.isVideoOff;
  const pictureInPictureHandler = adapter.pictureInPicture;

  // If we try to trigger PiP before the video metadata has loaded we'll get an error
  useEffect(
    function listenForVideoReady() {
      const video = videoRef.current;
      if (video) {
        return pictureInPictureHandler.onVideoReady(video, () => setVideoMetadataLoaded(true));
      }
    },
    [videoRef, pictureInPictureHandler],
  );

  useEffect(
    function listenForVideoMetadataUnloaded() {
      if (isRemoteVideoOff) {
        setVideoMetadataLoaded(false);
      }
    },
    [isRemoteVideoOff],
  );

  useEffect(
    function triggerPictureInPicture() {
      const pictureInPictureIsAvailable =
        pictureInPictureHandler.isSupported && remoteParticipantIsPictureInPictureTarget && videoMetadataLoaded;

      if (!pictureInPictureIsAvailable) {
        return;
      }

      const tryToEnterPictureInPicture = async () => {
        const video = videoRef.current;
        const shouldEnterForScreenshare = isLocalScreenShareMode || shouldTriggerPictureInPicture;
        const shouldEnterAutomatically = !document.hasFocus();
        const pictureInPictureRequested = video && (shouldEnterAutomatically || shouldEnterForScreenshare);

        if (pictureInPictureRequested) {
          try {
            await pictureInPictureHandler.enter(video);
            logger.info('Entered picture in picture', { remoteParticipantId });
          } catch (error) {
            pictureInPictureHandler.exit();
            logger.warn('Could not initiate picture in picture', {}, error as Error);
          } finally {
            setShouldTriggerPictureInPicture(false);
          }
        }
      };
      // Immediately try to enter picture in picture if the remote participant is our PiP target
      tryToEnterPictureInPicture();

      try {
        // Handles opening picture in picture when a user changes tabs
        window.navigator.mediaSession.setActionHandler(
          'enterpictureinpicture' as MediaSessionAction,
          tryToEnterPictureInPicture,
        );
      } catch (error) {
        logger.warn('Could not listen for enterpictureinpicture', {}, error as Error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      pictureInPictureHandler,
      remoteParticipantIsPictureInPictureTarget,
      videoRef,
      videoMetadataLoaded,
      shouldTriggerPictureInPicture,
      setShouldTriggerPictureInPicture,
      isLocalScreenShareMode,
    ],
  );

  /**
   * Handles exiting out of picture in picture when the user stops
   * screensharing
   */
  useEffect(
    function exitPipWhenScreenShareStops() {
      if (!isLocalScreenShareMode) {
        pictureInPictureHandler.exit();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLocalScreenShareMode, pictureInPictureHandler],
  );
}
